import * as React from 'react';
import LessonPlanApiService from 'src/api/LessonPlanApiController';
import LessonPlanDTO from 'src/models/lessonplan/LessonPlanDTO';
import { RouteComponentProps } from 'react-router-dom';
import DataTable, { DataTableColumnProps, Filterer } from 'src/components/common/DataTable';
import bindAllOfThis from 'src/utils/BindThisHelper';
import { ReduxStoreModel } from 'src/redux/ReduxModel';
import { withLocalize, LocalizeContextProps } from 'react-localize-redux';
import { connect } from 'react-redux';
import { PageProps } from 'src/models/common/ComponentProps';
import { UserState } from 'src/redux/UserReducer';
import TableRequestDTO from 'src/models/datatable/TableRequestDTO';
import TableResponseDTO from 'src/models/datatable/TableResponseDTO';
import Routes from 'src/core/Routes';
import LessonPlanDownloadRowViewModel from 'src/models/lessonplan/LessonPlanDownloadRowViewModel';
import Radio, { RadioChangeEvent } from 'antd/lib/radio';
import { Grades, Grade } from 'src/constants/Grades';
import { Planets } from 'src/constants/Planets';
import { Row, Col, Icon } from 'antd';
import Config from 'src/constants/Config';

interface LessonPlanProps extends PageProps<{}>, LocalizeContextProps {
    // From Redux
    User: UserState;
}

interface LessonPlansState {
    selectedGrade: Grade;
    allTableRecords: LessonPlanDownloadRowViewModel[];
    filteredTableRecords: LessonPlanDownloadRowViewModel[];
    loading: boolean;
}

class LessonPlans extends React.Component<LessonPlanProps, LessonPlansState> {
    private dataTableRef: DataTable<LessonPlanDownloadRowViewModel>;

    constructor(props: LessonPlanProps) {
        super(props);
        bindAllOfThis(this, LessonPlans.prototype);

        this.fetchData = this.fetchData.bind(this);

        // You are allowed to set defaults here, based on your state object above
        this.state = {
            selectedGrade: Grades.First,
            allTableRecords: [],
            filteredTableRecords: [],
            loading: false
        };
    }

    handleGradeChange(value: RadioChangeEvent) {
        this.setState(
            {
                selectedGrade: Grades.All.find(i => i.id === value.target.value),
                filteredTableRecords: this.state.allTableRecords.filter(i => i.gradeLevelId === value.target.value)
            },
            () => {
                this.dataTableRef.refresh();
            }
        );
    }

    componentDidMount() {
        this.fetchData();
    }

    render() {
        return (
            <div className="lesson-plans">
                <h2>{this.props.translate('admin.lessonPlans.title')}</h2>
                <p>{this.props.translate('admin.lessonPlans.description')}</p>
                {this.renderGrid()}
                {this.renderDownloadAll()}
                <DataTable
                    ref={ref => (this.dataTableRef = ref)}
                    serverSide={false}
                    tableProps={{ rowKey: 'rowKey', loading: this.state.loading }}
                    columns={this.customTableColumns()}
                    data={this.state.filteredTableRecords}
                />
            </div>
        );
    }

    private renderGrades() {
        var grades = [];
        grades.push(Grades.First, Grades.Second, Grades.Third, Grades.Fourth, Grades.Fifth);
        return (
            <Radio.Group value={grades.find((g: Grade) => g.id === this.state.selectedGrade.id).id} onChange={this.handleGradeChange} buttonStyle="solid">
                {grades.map(grade => (
                    <Radio.Button value={grade.id} key={grade.id}>
                        {grade.name}
                    </Radio.Button>
                ))}
            </Radio.Group>
        );
    }

    private renderGrid() {
        let items: { name: string; value: any }[] = [{ name: this.props.translate('common.grade').toString(), value: this.renderGrades() }];

        return items.map(item => {
            return (
                <Row key={item.name} className="grid-items">
                    <Col sm={4} md={6} lg={4} xl={2}>
                        {item.name}
                    </Col>
                    <Col sm={20} md={18} lg={20} xl={22}>
                        {item.value}
                    </Col>
                </Row>
            );
        });
    }

    private renderDownloadAll() {
        let link: JSX.Element = <p></p>;
        if (
            this.state.filteredTableRecords.some(
                i =>
                    i.postLaunchDisplayName !== this.props.translate('admin.lessonPlans.comingSoon').toString() ||
                    i.preLaunchDisplayName !== this.props.translate('admin.lessonPlans.comingSoon').toString()
            )
        ) {
            let azureUrl = Config.GetAzureUrl(`galaxy_lesson-plans_grade-${this.state.selectedGrade.level}.zip`, 'lesson-plan', this.props.User.cultureCode);
            link = <p><a href={azureUrl}>{this.props.translate('admin.lessonPlans.downloadAll').toString()}</a></p>;
        }

        return link;
    }

    private fetchData(): void {
        this.setState({ loading: true });

        let lessonPlanPromise = LessonPlanApiService.getAllLessonPlans();

        Promise.all([lessonPlanPromise]).then(response => {
            let lessonPlans = response[0].data;

            let tableRecords = new Array<LessonPlanDownloadRowViewModel>();

            for (let grade of Grades.All) {
                for (let planet of Planets.All) {
                    if (planet.id > 0) {
                        let tableRecord = {} as LessonPlanDownloadRowViewModel;

                        tableRecord.rowKey = `${grade.id}-${planet.id}`;
                        tableRecord.gradeLevel = grade.name;
                        tableRecord.gradeLevelId = grade.id;
                        tableRecord.planetId = planet.id;
                        tableRecord.planetName = planet.name;
                        tableRecord.cultureName = this.props.User.cultureCode;

                        let preLaunch = lessonPlans.find(i => i.cultureId === this.props.User.cultureId && i.gradeLevelId === grade.id && i.planetId === planet.id && i.isPreLaunch);

                        if (preLaunch != null) {
                            tableRecord.preLaunchDisplayName = preLaunch.displayName;
                            tableRecord.preLaunchFileName = `${preLaunch.fileName}`;
                        } else {
                            tableRecord.preLaunchDisplayName = this.props.translate('admin.lessonPlans.comingSoon').toString();
                            tableRecord.preLaunchFileName = '';
                        }

                        let postLaunch = lessonPlans.find(i => i.cultureId === this.props.User.cultureId && i.gradeLevelId === grade.id && i.planetId === planet.id && !i.isPreLaunch);

                        if (postLaunch != null) {
                            tableRecord.postLaunchDisplayName = postLaunch.displayName;
                            tableRecord.postLaunchFileName = `${postLaunch.fileName}`;
                        } else {
                            tableRecord.postLaunchDisplayName = this.props.translate('admin.lessonPlans.comingSoon').toString();
                            tableRecord.postLaunchFileName = '';
                        }

                        tableRecords.push(tableRecord);
                    }
                }
            }

            this.setState({ allTableRecords: tableRecords, filteredTableRecords: tableRecords.filter(i => i.gradeLevelId === this.state.selectedGrade.id), loading: false }, () => {
                this.dataTableRef.refresh();
            });
        });
    }

    private customTableColumns(): DataTableColumnProps<LessonPlanDownloadRowViewModel>[] {
        let preLaunchLink = (text, record: LessonPlanDownloadRowViewModel) => {
            return record.preLaunchFileName;
        };

        let postLaunchLink = (text, record: LessonPlanDownloadRowViewModel) => {
            return record.preLaunchFileName;
        };

        var columns = [
            DataTable.StandardColumns.Text('common.planet', 'planetName'),
            {
                columnProps: {
                    title: this.props.translate('admin.lessonPlans.documentTableHeaderColumn2'),
                    dataIndex: 'preLaunchDisplayName',
                    render: (text, record: LessonPlanDownloadRowViewModel) => {
                        if (record.preLaunchDisplayName !== this.props.translate('admin.lessonPlans.comingSoon').toString()) {
                            return (
                                <span>
                                    <Icon type="file-pdf" /> <a href={Config.GetAzureUrl(record.preLaunchFileName, 'lesson-plan', record.cultureName)}>{record.preLaunchDisplayName}</a>
                                </span>
                            );
                        } else {
                            return record.preLaunchDisplayName;
                        }
                    }
                }
            },
            {
                columnProps: {
                    title: this.props.translate('admin.lessonPlans.documentTableHeaderColumn3'),
                    dataIndex: 'postLaunchDisplayName',
                    render: (text, record: LessonPlanDownloadRowViewModel) => {
                        if (record.postLaunchDisplayName !== this.props.translate('admin.lessonPlans.comingSoon').toString()) {
                            return (
                                <span>
                                    <Icon type="file-pdf" /> <a href={Config.GetAzureUrl(record.postLaunchFileName, 'lesson-plan', record.cultureName)}>{record.postLaunchDisplayName}</a>
                                </span>
                            );
                        } else {
                            return record.postLaunchDisplayName;
                        }
                    }
                }
            }
        ];

        return columns;
    }
}

function mapStateToProps(state: ReduxStoreModel) {
    return {
        User: state.User
    };
}

export default connect(mapStateToProps)(withLocalize(LessonPlans));



// WEBPACK FOOTER //
// ./src/pages/admin/LessonPlans.tsx